// Client entry point for the Pulse RAG/AI layer.
// Invokes the `pulse-query` Supabase edge function. The edge function
// classifies, fetches context, and calls Claude server-side. The Anthropic
// API key never touches the browser.

import { supabase } from "@/integrations/supabase/client";
import type { PulseContext } from "@/hooks/usePulse";

export interface PulseAIResponse {
  answer: string;
  intent: string;
  domain: string;
  confidence: "high" | "medium" | "low" | "no_data";
  resolvedPlayerId?: string | null;
  resolvedPlayerName?: string | null;
  dataPoints: Record<string, unknown> | null;
  followUpSuggestions: string[];
  ruleBased: boolean;
}

export async function askPulseAI(
  question: string,
  context: PulseContext,
  intent: string,
  previousResponse?: string | null,
): Promise<PulseAIResponse> {
  const { data, error } = await supabase.functions.invoke("pulse-query", {
    body: { question, intent, context, previousResponse: previousResponse || undefined },
  });
  if (error) {
    throw new Error(error.message || "pulse-query failed");
  }
  if (!data || typeof data !== "object") {
    throw new Error("pulse-query returned an unexpected response");
  }
  if ("error" in data && typeof (data as { error: unknown }).error === "string") {
    throw new Error((data as { error: string }).error);
  }
  return data as PulseAIResponse;
}
